/**
 * Analytics utility — wraps PostHog (posthog-js) and Sentry (@sentry/react).
 *
 * All functions are no-ops when the corresponding env var is absent, so
 * analytics are automatically disabled in local dev if you leave those
 * vars out of your .env.
 */
import posthog from 'posthog-js';
import * as Sentry from '@sentry/react';

export function initAnalytics() {
  const phKey = import.meta.env.VITE_POSTHOG_KEY as string | undefined;
  const phHost = (import.meta.env.VITE_POSTHOG_HOST as string | undefined) ?? 'https://us.i.posthog.com';
  const sentryDsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;

  if (phKey) {
    posthog.init(phKey, {
      api_host: phHost,
      person_profiles: 'identified_only',
    });
  }

  if (sentryDsn) {
    Sentry.init({
      dsn: sentryDsn,
      environment: import.meta.env.MODE, // 'development' | 'production'
      integrations: [Sentry.browserTracingIntegration()],
      // Capture 10% of transactions in prod; 100% in dev
      tracesSampleRate: import.meta.env.PROD ? 0.1 : 1.0,
    });
  }
}

/** Call after the user signs in so events are tied to a persistent user. */
export function identifyUser(userId: string, email: string, orgId: string) {
  posthog.identify(userId, { email, org_id: orgId });
  if (orgId) {
    posthog.group('organization', orgId);
  }
  posthog.capture('user_logged_in');

  // Sentry: attaches user context to every subsequent error
  Sentry.setUser({ id: userId, email });
}

/** Call on sign-out to clear PII from PostHog and Sentry's scope. */
export function clearUser() {
  posthog.reset();
  Sentry.setUser(null);
}

/**
 * Track any custom event.
 * @param name  snake_case event name (e.g. 'file_upload_started')
 * @param params  optional dimensions / metrics
 */
export function trackEvent(
  name: string,
  params?: Record<string, string | number | boolean>,
) {
  posthog.capture(name, params);
}

/**
 * Track a successful file upload.
 * @param fileCount  number of files in this batch
 * @param caseId  the case these files belong to
 */
export function trackFileUpload(fileCount: number, caseId: string) {
  posthog.capture('file_upload_success', {
    file_count: fileCount,
    case_id: caseId,
  });
}

/**
 * Track a file upload failure and forward the error to Sentry.
 * @param caseId  the case the upload was attempted for
 * @param errorMessage  human-readable error description
 */
export function trackUploadError(caseId: string, errorMessage: string) {
  posthog.capture('file_upload_error', { case_id: caseId });
  Sentry.captureMessage(`Upload error: ${errorMessage}`, {
    level: 'warning',
    extra: { caseId },
  });
}

/**
 * Forward a caught JS Error to Sentry with optional extra context.
 * Use this instead of console.error for non-trivial caught exceptions.
 */
export function captureError(
  error: Error,
  context?: Record<string, unknown>,
) {
  Sentry.captureException(error, { extra: context });
}
